import { useEffect, useMemo, useState } from "react";
import { MdGroups, MdSchool, MdEmojiEvents } from "react-icons/md";
import { getRanking } from "../services/rankingService";
import { BrandButton } from "../components/ui/BrandButton";
import { useAuth } from "../context/AuthContext";
import { useAppLoading } from "../context/AppLoadingContext";
const PAGE_SIZE = 20;

const scopeLabels = {
  general: "General",
  tnua: "Por tnua",
  institution: "Por escuela",
};

function formatRank(rank) {
  return `#${rank}`;
}

function formatPoints(points) {
  const safePoints = Number.isFinite(points) ? points : 0;
  return `${safePoints} punto${safePoints === 1 ? "" : "s"}`;
}

function formatDisplayName(name, lastName) {
  const safeName = String(name ?? "").trim();
  const safeLastName = String(lastName ?? "").trim();

  if (!safeName && !safeLastName) {
    return "";
  }

  if (!safeLastName) {
    return safeName;
  }

  const lastNameInitial = safeLastName.charAt(0).toUpperCase();
  return `${safeName} ${lastNameInitial}.`;
}

function getScopeIcon(scope) {
  if (scope === "tnua") {
    return <MdGroups aria-hidden="true" className="h-4 w-4" />;
  }

  if (scope === "institution") {
    return <MdSchool aria-hidden="true" className="h-4 w-4" />;
  }

  return <MdEmojiEvents aria-hidden="true" className="h-4 w-4" />;
}

function RankingScopeButton({ scope, currentScope, onClick }) {
  const isActive = scope === currentScope;

  return (
    <button
      type="button"
      onClick={onClick}
      className={[
        "inline-flex items-center gap-2 rounded-full px-4 py-2 text-sm font-semibold transition-colors",
        isActive
          ? "bg-brand-primary text-white shadow-sm"
          : "bg-white text-brand-primary hover:bg-brand-primary/5",
      ].join(" ")}
    >
      {getScopeIcon(scope)}
      <span>{scopeLabels[scope]}</span>
    </button>
  );
}

export function RankingPage() {
  const { trackAsync } = useAppLoading();
  const { user } = useAuth();
  const [scope, setScope] = useState("general");
  const [ranking, setRanking] = useState([]);
  const [viewer, setViewer] = useState(null);
  const [pagination, setPagination] = useState({
    page: 1,
    pageSize: PAGE_SIZE,
    total: 0,
    totalPages: 0,
  });
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState("");
  const [page, setPage] = useState(1);
  const userInstitution = user?.institution ?? null;
  const userTnua = user?.tnua ?? null;
  const availableScopes = useMemo(
    () =>
      [
        { id: "general", label: "General", icon: <MdEmojiEvents aria-hidden="true" className="h-4 w-4" /> },
        userTnua
          ? {
              id: "tnua",
              label: "Por tnua",
              icon: <MdGroups aria-hidden="true" className="h-4 w-4" />,
            }
          : null,
        userInstitution
          ? {
              id: "institution",
              label: "Por escuela",
              icon: <MdSchool aria-hidden="true" className="h-4 w-4" />,
            }
          : null,
      ].filter(Boolean),
    [userInstitution, userTnua],
  );

  const activeFilterLabel = useMemo(() => {
    if (scope === "tnua") {
      return userTnua?.name ?? "";
    }

    if (scope === "institution") {
      return userInstitution?.name ?? "";
    }

    return "";
  }, [scope, userInstitution, userTnua]);

  useEffect(() => {
    setPage(1);
  }, [scope]);

  useEffect(() => {
    let isMounted = true;

    const loadRanking = async () => {
      if ((scope === "tnua" && !userTnua) || (scope === "institution" && !userInstitution)) {
        if (!isMounted) {
          return;
        }

        setRanking([]);
        setPagination({
          page: 1,
          pageSize: PAGE_SIZE,
          total: 0,
          totalPages: 0,
        });
        setError("");
        setIsLoading(false);
        return;
      }

      setIsLoading(true);
      setError("");

      try {
        const response = await getRanking({
          page,
          pageSize: PAGE_SIZE,
          institutionId: scope === "institution" ? userInstitution.id : "",
          tnuaId: scope === "tnua" ? userTnua.id : "",
        });

        if (!isMounted) {
          return;
        }

        setRanking(response.ranking ?? []);
        setViewer(response.viewer ?? null);
        setPagination(response.pagination ?? pagination);
      } catch (requestError) {
        if (!isMounted) {
          return;
        }

        setError(requestError.message || "No pudimos cargar el ranking.");
      } finally {
        if (isMounted) {
          setIsLoading(false);
        }
      }
    };

    void trackAsync(loadRanking);

    return () => {
      isMounted = false;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [page, scope, userInstitution, userTnua, trackAsync]);

  const missingFilter = (scope === "tnua" && !userTnua) || (scope === "institution" && !userInstitution);

  const canGoBack = pagination.page > 1;
  const canGoNext = pagination.totalPages > 0 && pagination.page < pagination.totalPages;

  return (
    <div className="grid gap-5">
      <section className="px-1 py-1 sm:px-2 sm:py-2">
        <h2 className="font-display text-3xl text-brand-primary sm:text-4xl">
          Tabla de posiciones
        </h2>
      </section>

      <section className="grid gap-4 rounded-[2rem] border border-brand-secondary/15 bg-white p-4 sm:p-5">
        <div className="flex flex-wrap items-center gap-2">
          {availableScopes.map(({ id }) => (
            <RankingScopeButton
              key={id}
              scope={id}
              currentScope={scope}
              onClick={() => setScope(id)}
            />
          ))}
          {viewer ? (
            <span className="inline-flex items-center gap-2 rounded-full bg-brand-accent-light/20 px-3 py-2 text-sm font-bold text-brand-primary">
              <span>{formatRank(viewer.rank)}</span>
              <span className="text-brand-primary/60">·</span>
              <span>{formatPoints(viewer.points)}</span>
            </span>
          ) : null}
        </div>

        {viewer ? (
          <div className="grid gap-1 rounded-2xl border border-brand-secondary/15 bg-brand-surface px-4 py-3 text-sm text-brand-primary">
            <span className="font-semibold">Tu puntaje total: {formatPoints(viewer.points)}</span>
          </div>
        ) : null}

        {scope !== "general" && activeFilterLabel ? (
          <div className="max-w-xl rounded-2xl border border-brand-secondary/15 bg-brand-surface px-4 py-3 text-sm font-medium text-brand-primary">
            {scope === "tnua" ? "Tu tnua" : "Tu escuela"}: {activeFilterLabel}
          </div>
        ) : null}
      </section>

      {isLoading ? (
        <div className="rounded-[2rem] border border-brand-secondary/15 bg-white px-6 py-10 text-center text-sm text-slate-500 shadow-soft">
          Cargando ranking...
        </div>
      ) : error ? (
        <div className="rounded-[2rem] border border-rose-200 bg-rose-50 px-6 py-10 text-center text-sm text-rose-700 shadow-soft">
          {error}
        </div>
      ) : (
        <section className="grid gap-4">
          <div className="flex items-center justify-between gap-4">
            {activeFilterLabel ? (
              <p className="text-sm text-slate-600">
                {scopeLabels[scope]}: {activeFilterLabel}
              </p>
            ) : null}
            <p className="text-sm font-semibold text-brand-primary">
              Página {pagination.page} de {pagination.totalPages || 1}
            </p>
          </div>

          {missingFilter ? (
            <div className="rounded-[2rem] border border-brand-secondary/15 bg-white px-6 py-10 text-center text-sm text-slate-500 shadow-soft">
              No tenés cargada una {scope === "tnua" ? "tnua" : "escuela"} para ver ese ranking.
            </div>
          ) : ranking.length === 0 ? (
            <div className="rounded-[2rem] border border-brand-secondary/15 bg-white px-6 py-10 text-center text-sm text-slate-500 shadow-soft">
              Todavía no hay puntajes para mostrar.
            </div>
          ) : (
            <div className="grid gap-3">
              {ranking.map((entry) => {
                const isTopThree = entry.rank <= 3;

                return (
                  <article
                    key={entry.id}
                    className={[
                      "flex items-center gap-2 rounded-[1.6rem] border bg-white px-3 py-2.5 shadow-sm transition-shadow hover:shadow-md sm:grid-cols-[auto_1fr_auto] sm:gap-4 sm:p-4",
                      isTopThree ? "border-brand-primary/20" : "border-brand-secondary/15",
                    ].join(" ")}
                  >
                    <div className="flex min-w-0 flex-1 items-center gap-2 sm:gap-4">
                      <div
                        className={[
                          "flex h-10 w-10 shrink-0 items-center justify-center rounded-full text-sm font-bold sm:h-12 sm:w-12 sm:text-base",
                          entry.rank === 1
                            ? "bg-brand-gold-soft text-brand-primary"
                            : entry.rank === 2
                              ? "bg-brand-secondary text-white"
                              : entry.rank === 3
                                ? "bg-brand-secondary text-white"
                                : "bg-brand-accent-light/20 text-brand-primary",
                        ].join(" ")}
                      >
                        {formatRank(entry.rank)}
                      </div>
                      <div className="min-w-0 flex-1">
                        <p className="truncate font-sans text-sm font-bold leading-tight text-brand-primary sm:text-xl">
                          {formatDisplayName(entry.name, entry.lastName)}
                        </p>
                      </div>
                    </div>

                    <div className="shrink-0 whitespace-nowrap text-right leading-none">
                      <p className="text-sm font-bold text-brand-primary sm:text-2xl">
                        {formatPoints(entry.points)}
                      </p>
                    </div>
                  </article>
                );
              })}
            </div>
          )}

          {pagination.totalPages > 1 ? (
            <div className="flex flex-col gap-3 rounded-[1.6rem] border border-brand-secondary/15 bg-brand-surface px-4 py-3 sm:flex-row sm:items-center sm:justify-between">
              <p className="text-sm text-slate-600">
                Mostrando {ranking.length} de {pagination.total} jugadores · Página{" "}
                {pagination.page} de {pagination.totalPages}
              </p>
              <div className="flex items-center gap-2">
                <BrandButton
                  type="button"
                  onClick={() => setPage((current) => Math.max(1, current - 1))}
                  disabled={!canGoBack}
                  variant="outline"
                  radius="full"
                  size="md"
                  motion="subtle"
                >
                  Anterior
                </BrandButton>
                <BrandButton
                  type="button"
                  onClick={() =>
                    setPage((current) =>
                      pagination.totalPages > 0
                        ? Math.min(pagination.totalPages, current + 1)
                        : current + 1,
                    )
                  }
                  disabled={!canGoNext}
                  variant="outline"
                  radius="full"
                  size="md"
                  motion="subtle"
                >
                  Siguiente
                </BrandButton>
              </div>
            </div>
          ) : null}
        </section>
      )}
    </div>
  );
}
